'use client';

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import SuccessToast from './SuccessToast';
import { useAuth } from './AuthProvider';
import { SaveUnconfirmedError } from '../../lib/generation-v3-library';
import { ExportProgressToast } from './ExportProgressToast';
import { warnBeforeExportUnload, type ExportProgress, type ReportExportProgress } from '../../lib/export-progress';

type ToastAction = { href: string; label: string };
const ToastContext = createContext<((message: string, action?: ToastAction) => void) | null>(null);
type ExportContextValue = {
  exporting: boolean;
  runExport: <T>(task: (report: ReportExportProgress, signal: AbortSignal) => Promise<T>, cardCount?: number) => Promise<T>;
};
const ExportContext = createContext<ExportContextValue | null>(null);
type SaveContextValue = {
  saving: boolean;
  confirmSave: (operationId: string, action: ToastAction) => void;
  runSave: <T>(task: (signal: AbortSignal) => Promise<T>, cardCount?: number, getAction?: (result: T) => ToastAction) => Promise<T>;
};
const SaveContext = createContext<SaveContextValue | null>(null);

/** Mounted in the root layout so confirmations survive client-side navigation. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const userId = useAuth().user?.id;
  const [ownerId, setOwnerId] = useState(userId);
  const epoch = useRef(0);
  const accountWork = useRef<AbortController | null>(null);
  if (accountWork.current === null) accountWork.current = new AbortController();
  const sequence = useRef(0);
  const [toast, setToast] = useState<{ id: number; message: string; action?: ToastAction } | null>(null);
  const exportBusy = useRef(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  const [exportError, setExportError] = useState('');
  const saveBusy = useRef(false);
  const [saveProgress, setSaveProgress] = useState<ExportProgress | null>(null);
  const [saveError, setSaveError] = useState<{ message: string; operationId?: string } | null>(null);
  useLayoutEffect(() => {
    if (accountWork.current!.signal.aborted) accountWork.current = new AbortController();
    const controller = accountWork.current!;
    epoch.current++;
    exportBusy.current = false; saveBusy.current = false;
    setToast(null); setExportProgress(null); setExportError(''); setSaveProgress(null); setSaveError(null);
    setOwnerId(userId);
    return () => controller.abort();
  }, [userId]);
  const showSuccess = useCallback((message: string, action?: ToastAction) => {
    setToast({ id: ++sequence.current, message, action });
  }, []);
  const id = toast?.id;
  const dismiss = useCallback(() => {
    setToast(current => current?.id === id ? null : current);
  }, [id]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (exportBusy.current || saveBusy.current) warnBeforeExportUnload(event); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  const runExport = useCallback(async <T,>(task: (report: ReportExportProgress, signal: AbortSignal) => Promise<T>, cardCount?: number): Promise<T> => {
    if (exportBusy.current) throw new Error('An export is already in progress. Wait for your download to start.');
    if (saveBusy.current) throw new Error('Wait for your deck to finish saving.');
    const version = epoch.current;
    const signal = accountWork.current!.signal;
    signal.throwIfAborted();
    exportBusy.current = true;
    setExportError('');
    setSaveError(null);
    setToast(null);
    setExportProgress({ stage: 'collecting', cardCount });
    try {
      const result = await task(progress => {
        if (version === epoch.current) setExportProgress(previous => ({ ...previous, ...progress }));
      }, signal);
      signal.throwIfAborted();
      if (version === epoch.current) showSuccess('Your .apkg download has started.');
      return result;
    } catch (error) {
      if (version === epoch.current && !(error instanceof Error && error.name === 'AbortError')) {
        setExportError(error instanceof Error ? error.message : 'Please try exporting again.');
      }
      throw error;
    } finally {
      if (version === epoch.current) { exportBusy.current = false; setExportProgress(null); }
    }
  }, [showSuccess]);
  const exportContext = useMemo(() => ({ runExport, exporting: Boolean(exportProgress) }), [runExport, exportProgress]);
  const runSave = useCallback(async <T,>(task: (signal: AbortSignal) => Promise<T>, cardCount?: number, getAction?: (result: T) => ToastAction): Promise<T> => {
    if (saveBusy.current) throw new Error('A deck is already being saved. Please wait.');
    if (exportBusy.current) throw new Error('Wait for your download to start.');
    const version = epoch.current;
    const signal = accountWork.current!.signal;
    signal.throwIfAborted();
    saveBusy.current = true;
    setSaveError(null); setExportError(''); setToast(null);
    setSaveProgress({ stage: 'preparing', cardCount });
    try {
      const result = await task(signal);
      signal.throwIfAborted();
      if (version === epoch.current) showSuccess('Deck saved to your library.', getAction?.(result));
      return result;
    } catch (error) {
      if (version === epoch.current && !(error instanceof Error && error.name === 'AbortError')) {
        setSaveError({ message: error instanceof Error ? error.message : 'Please try saving again.',
          operationId: error instanceof SaveUnconfirmedError ? error.operationId : undefined });
      }
      throw error;
    } finally {
      if (version === epoch.current) { saveBusy.current = false; setSaveProgress(null); }
    }
  }, [showSuccess]);
  const confirmSave = useCallback((operationId: string, action: ToastAction) => {
    if (saveError?.operationId !== operationId) return;
    setSaveError(null);
    showSuccess('Deck saved to your library.', action);
  }, [saveError, showSuccess]);
  const saveContext = useMemo(() => ({ runSave, confirmSave, saving: Boolean(saveProgress) }), [runSave, confirmSave, saveProgress]);

  return <ToastContext.Provider value={showSuccess}><ExportContext.Provider value={exportContext}><SaveContext.Provider value={saveContext}>
    {children}
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{ownerId === userId && toast && <span key={toast.id}>{toast.message}</span>}</div>
    {ownerId === userId && toast && <SuccessToast key={toast.id} message={toast.message} action={toast.action} onDismiss={dismiss} announce={false} />}
    {ownerId === userId && <><ExportProgressToast progress={exportProgress} error={exportError} onDismiss={() => setExportError('')} />
    <ExportProgressToast operation="save" progress={saveProgress} error={saveError?.message || ''} unconfirmed={Boolean(saveError?.operationId)} onDismiss={() => setSaveError(null)} /></>}
  </SaveContext.Provider></ExportContext.Provider></ToastContext.Provider>;
}

export function useSaveProgress() {
  const context = useContext(SaveContext);
  if (!context) throw new Error('useSaveProgress requires ToastProvider');
  return context;
}

export function useExportProgress() {
  const context = useContext(ExportContext);
  if (!context) throw new Error('useExportProgress requires ToastProvider');
  return context;
}

export function useSuccessToast() {
  const showSuccess = useContext(ToastContext);
  if (!showSuccess) throw new Error('useSuccessToast requires ToastProvider');
  return showSuccess;
}
